<template>
  <div ref="containerRef" class="relative flex flex-col">
    <!-- Trigger -->
    <button
      type="button"
      :class="[
        'flex flex-1 w-full items-center gap-2.5 py-3 px-4 sm:min-h-[4rem] transition-colors text-left',
        isOpen ? 'bg-muted/60' : 'bg-transparent hover:bg-muted/40',
      ]"
      @click="togglePanel"
    >
      <Icon
        :name="selected ? selected.icon : 'tabler:search'"
        class="shrink-0 text-foreground"
        size="22"
      />
      <span class="min-w-0 flex-1 truncate text-base font-semibold text-foreground">
        {{ selected ? selected.label : 'What service?' }}
      </span>
      <button
        v-if="selected"
        type="button"
        class="shrink-0 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground"
        @click.stop="clearSelection"
      >
        <Icon name="tabler:x" size="14" />
      </button>
      <Icon
        name="tabler:chevron-down"
        size="14"
        :class="['shrink-0 text-muted-foreground/60 transition-transform duration-200', isOpen && 'rotate-180']"
      />
    </button>

    <!-- Panel — teleported to body so it escapes overflow-hidden ancestors -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="panelRef"
        :style="panelStyle"
        class="fixed z-[200] overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
        @mousedown.stop
      >
        <div class="py-3 px-3">
          <p class="mb-2 px-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Choose a service
          </p>
          <div class="grid grid-cols-2 gap-0.5 sm:grid-cols-3">
            <button
              v-for="service in SERVICE_TYPES"
              :key="service.key"
              type="button"
              :class="[
                'service-card flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors active:scale-[0.97]',
                selected?.key === service.key
                  ? 'bg-foreground text-background'
                  : 'hover:bg-muted/60',
              ]"
              @mousedown.prevent="select(service)"
            >
              <Icon
                :name="service.icon"
                size="22"
                class="shrink-0"
              />
              <span class="truncate text-sm font-semibold">{{ service.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
// Exports must live in a regular <script> block — <script setup> forbids ES module exports.
export interface ServiceType {
  key: string;
  label: string;
  icon: string;
}

export const SERVICE_TYPES: ServiceType[] = [
  { key: 'grooming', label: 'Grooming',    icon: 'tabler:scissors'    },
  { key: 'vet',      label: 'Vet Care',    icon: 'tabler:stethoscope' },
  { key: 'walking',  label: 'Dog Walking', icon: 'tabler:walk'        },
  { key: 'daycare',  label: 'Daycare',     icon: 'tabler:home-heart'  },
  { key: 'training', label: 'Training',    icon: 'tabler:trophy'      },
  { key: 'boarding', label: 'Boarding',    icon: 'tabler:bed'         },
];
</script>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { gsap } from 'gsap';

const emit = defineEmits<{
  select: [service: ServiceType];
  clear: [];
}>();

// ── Template refs ──────────────────────────────────────────────────────────────
const containerRef = ref<HTMLElement | null>(null);
const panelRef     = ref<HTMLElement | null>(null);

// ── State ─────────────────────────────────────────────────────────────────────
const isOpen   = ref(false);
const selected = ref<ServiceType | null>(null);
const panelStyle = ref<Record<string, string>>({});

// ── GSAP context ──────────────────────────────────────────────────────────────
let gsapCtx: gsap.Context | null = null;

// ── Panel positioning — aligned to the ancestor <form> ───────────────────────
function getFormRect(): DOMRect | null {
  let el: HTMLElement | null = containerRef.value;
  while (el && el.tagName !== 'FORM') el = el.parentElement;
  return el?.getBoundingClientRect() ?? null;
}

function updatePanelPosition() {
  const rect = getFormRect() ?? containerRef.value?.getBoundingClientRect();
  if (!rect) return;
  panelStyle.value = {
    top:   `${rect.bottom + 10}px`,
    left:  `${rect.left}px`,
    width: `${rect.width}px`,
  };
}

// ── Open / close ───────────────────────────────────────────────────────────────
async function openPanel() {
  if (isOpen.value) return;
  updatePanelPosition();
  isOpen.value = true;
  await nextTick();

  gsapCtx?.revert();
  if (panelRef.value) {
    gsapCtx = gsap.context(() => {
      gsap.from(panelRef.value!, {
        opacity: 0,
        y: -10,
        duration: 0.22,
        ease: 'power3.out',
      });
      gsap.from(panelRef.value!.querySelectorAll('.service-card'), {
        opacity: 0,
        y: 8,
        stagger: 0.04,
        duration: 0.2,
        ease: 'power2.out',
        clearProps: 'all',
      });
    });
  }
}

function closePanel() {
  if (!isOpen.value) return;
  isOpen.value = false;
  gsapCtx?.revert();
  gsapCtx = null;
}

function togglePanel() {
  if (isOpen.value) {
    closePanel();
  } else {
    openPanel();
  }
}

// ── Select / clear ─────────────────────────────────────────────────────────────
function select(service: ServiceType) {
  selected.value = service;
  closePanel();
  emit('select', service);
}

function clearSelection() {
  selected.value = null;
  emit('clear');
}

// ── Close on outside click ─────────────────────────────────────────────────────
function onDocumentClick(e: MouseEvent) {
  if (!isOpen.value) return;
  const target = e.target as Node;
  if (containerRef.value?.contains(target)) return;
  if (panelRef.value?.contains(target)) return;
  closePanel();
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true);
  window.addEventListener('scroll', updatePanelPosition, { passive: true });
  window.addEventListener('resize', updatePanelPosition, { passive: true });
});

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true);
  window.removeEventListener('scroll', updatePanelPosition);
  window.removeEventListener('resize', updatePanelPosition);
  gsapCtx?.revert();
});
</script>
